<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">
              알림톡 전송결과
            </h1>
          </div>
          <span class="pageDesc">알림톡 발송 내역을 조회하고 상태를 확인할 수 있습니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 콘텐츠 영역 -->
    <div class="card-body px-0 pt-3 pb-2">
      <!-- 메인 콘텐츠 카드 -->
      <div class="card content-wrap-card">
        <!-- 검색 필터 -->
        <AlimtalkSearchFilter @search="handleSearch" />
        
        <!-- 통계 카드 -->
        <div class="stats-cards" v-if="statsData">
          <a-row :gutter="16">
            <a-col :span="6">
              <a-card>
                <a-statistic
                  title="전체 발송"
                  :value="statsData.total"
                  suffix="건"
                />
              </a-card>
            </a-col>
            <a-col :span="6">
              <a-card>
                <a-statistic
                  title="성공"
                  :value="statsData.success"
                  :value-style="{ color: '#52c41a' }"
                  suffix="건"
                />
              </a-card>
            </a-col>
            <a-col :span="6">
              <a-card>
                <a-statistic
                  :value="statsData.failed"
                  :value-style="{ color: '#f5222d' }"
                  suffix="건"
                >
                  <template #title>
                    <span>
                      실패
                      <a-tooltip title="API 호출 실패, 인프라 점검 필요">
                        <i class="fa fa-question-circle" style="margin-left: 4px; color: #999; font-size: 12px; cursor: help;"></i>
                      </a-tooltip>
                    </span>
                  </template>
                </a-statistic>
              </a-card>
            </a-col>
            <a-col :span="6">
              <a-card style="position: relative;">
                <a-statistic
                  :value="statsData.error"
                  :value-style="{ color: '#faad14' }"
                  suffix="건"
                >
                  <template #title>
                    <span>
                      오류
                      <a-tooltip title="비즈메세지 파라미터 오류, 파라미터 점검 필요">
                        <i class="fa fa-question-circle" style="margin-left: 4px; color: #999; font-size: 12px; cursor: help;"></i>
                      </a-tooltip>
                    </span>
                  </template>
                </a-statistic>
                <a-button 
                  size="small" 
                  type="link"
                  @click="handleOpenErrorDocs"
                  style="position: absolute; top: 8px; right: 8px; padding: 2px 6px; height: auto; font-size: 11px; white-space: nowrap;"
                  title="오류코드 문서 보기"
                >
                  <span style="font-size: 10px;">오류문서보기</span>
                  <i class="fa fa-external-link" style="margin-left: 3px; font-size: 10px;"></i>
                </a-button>
              </a-card>
            </a-col>
          </a-row>
        </div>
        
        <!-- 결과 테이블 -->
        <AlimtalkResultsTable
          :data-source="tableData"
          :loading="loading"
          :total="total"
          :current-page="currentPage"
          :page-size="pageSize"
          @change="handleTableChange"
          @viewDetail="handleViewDetail"
        />
      </div>
    </div>
    <!-- 상세 조회 드로워 -->
    <AlimtalkDetailDrawer
      :visible="detailDrawerVisible"
      :detail="selectedDetail"
      :loading="detailLoading"
      @close="handleCloseDetail"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import AlimtalkSearchFilter from './components/AlimtalkSearchFilter.vue';
import AlimtalkResultsTable from './components/AlimtalkResultsTable.vue';
import AlimtalkDetailDrawer from './components/AlimtalkDetailDrawer.vue';
import { alimtalkApi } from './alimtalkApi';

export default {
  name: 'AlimtalkResults',
  components: {
    AlimtalkSearchFilter,
    AlimtalkResultsTable,
    AlimtalkDetailDrawer
  },
  setup() {
    const loading = ref(false);
    const detailLoading = ref(false);
    const tableData = ref([]);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const searchParams = ref({});
    
    const statsData = reactive({
      total: 0,
      success: 0,
      failed: 0,
      error: 0
    });
    
    const detailDrawerVisible = ref(false);
    const selectedDetail = ref(null);

    // 데이터 조회 (로그 + 통계 통합)
    const fetchData = async (params = {}) => {
      loading.value = true;
      try {
        const mergedParams = {
          ...searchParams.value,
          ...params,
          page: params.page || currentPage.value,
          limit: params.limit || pageSize.value,
          includeStats: true // 통계 포함
        };

        // 통합 API 호출
        const response = await alimtalkApi.getLogs(mergedParams);
        
        if (response.data) {
          // 로그 데이터 처리
          if (response.data.logs) {
            tableData.value = response.data.logs.data || [];
            total.value = response.data.logs.total || 0;
            currentPage.value = response.data.logs.page || 1;
            pageSize.value = response.data.logs.limit || 20;
          }

          // 통계 데이터 처리
          if (response.data.statistics?.summary) {
            const summary = response.data.statistics.summary;
            
            // 필터가 적용된 경우, total 값은 조회된 로그의 총 개수로 설정
            // (백엔드에서 필터된 통계를 제공하지 않는 경우의 임시 방안)
            const hasFilter = mergedParams.templateCode || mergedParams.status || 
                            mergedParams.mobile || mergedParams.eventType;
            
            if (hasFilter && response.data.logs) {
              // 필터 적용 시: 조회된 전체 개수를 total로 사용
              statsData.total = response.data.logs.total || 0;
              
              // TODO: 백엔드에서 필터된 통계 제공 시 아래 코드 제거
              // 현재는 status별 집계를 프론트에서 할 수 없으므로 
              // 전체 통계를 그대로 표시 (정확하지 않음)
              statsData.success = summary.success || 0;
              statsData.failed = summary.failed || 0;
              statsData.error = summary.error || 0;
            } else {
              // 필터 없음: 전체 통계 사용
              statsData.total = summary.total || 0;
              statsData.success = summary.success || 0;
              statsData.failed = summary.failed || 0;
              statsData.error = summary.error || 0;
            }
          }
        }
      } catch (error) {
        console.error('데이터 조회 실패:', error);
        message.error('데이터를 불러오는데 실패했습니다.');
      } finally {
        loading.value = false;
      }
    };

    // 검색 처리
    const handleSearch = (params) => {
      searchParams.value = params;
      currentPage.value = 1;
      fetchData({ ...params, page: 1 });
    };

    // 테이블 변경 처리 (페이지네이션, 정렬, 필터)
    const handleTableChange = (params) => {
      currentPage.value = params.page;
      pageSize.value = params.limit;
      fetchData(params);
    };

    // 상세 조회
    const handleViewDetail = async (record) => {
      detailDrawerVisible.value = true;
      detailLoading.value = true;
      selectedDetail.value = null;
      
      try {
        const response = await alimtalkApi.getLogDetail(record._id);
        selectedDetail.value = response.data;
      } catch (error) {
        console.error('상세 조회 실패:', error);
        message.error('상세 정보를 불러오는데 실패했습니다.');
      } finally {
        detailLoading.value = false;
      }
    };

    // 상세 드로워 닫기
    const handleCloseDetail = () => {
      detailDrawerVisible.value = false;
      selectedDetail.value = null;
    };

    // 오류코드 문서 열기 (새 탭)
    const handleOpenErrorDocs = () => {
      window.open('https://docs.nhncloud.com/ko/Notification/KakaoTalk%20Bizmessage/ko/error-code/', '_blank');
    };

    onMounted(() => {
      // 컴포넌트 마운트 시 초기 데이터 로드는 SearchFilter에서 처리
    });

    return {
      loading,
      detailLoading,
      tableData,
      total,
      currentPage,
      pageSize,
      statsData,
      detailDrawerVisible,
      selectedDetail,
      fetchData,
      handleSearch,
      handleTableChange,
      handleViewDetail,
      handleCloseDetail,
      handleOpenErrorDocs
    };
  }
};
</script>

<style scoped>
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
}

.pageDesc {
  font-size: 14px;
  color: #6c757d;
  margin-top: 8px;
  display: block;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.content-wrap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  padding: 20px;
}

.stats-cards {
  margin: 20px 0;
  padding: 0 20px;
}

.stats-cards .ant-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

:deep(.ant-statistic-title) {
  font-size: 14px;
  color: #666;
}

:deep(.ant-statistic-content) {
  font-size: 24px;
  font-weight: 600;
}

/* 테이블 영역 패딩 조정 */
:deep(.results-table-container) {
  background: transparent;
  padding: 0 20px 20px;
  box-shadow: none;
}
</style>